import React, { ReactElement } from "react"
import AutomationResumeDetails from "./AutomationResumeDetails"
import ViewQuestionnaire from "../../../questionnaire/ViewQuestionnaire"
import { Button, CircularProgress, Tab, Tabs } from "@mui/material"
import { observer } from "mobx-react-lite"
import { Automator } from "../../../../../models/Automator"
import { store } from "../../../../../stores/Store"
import { AlertModal, DisplayValue } from "../../../../.."
import AutomationAnswers from "./AutomationAnswers"
import { Icons } from "../../../../../misc/Icons"
import { Theme } from "../../../../../misc/Theme"
import FormAutomationChart from "../../../../../misc/Charts"
import InlineMenu from "../../../../../misc/InlineMenu"
import { downloadBase64File } from "../../../../../misc/download"
import { DataSource, NodeReference } from "../../../../../models/Interfaces"
import AutomationStatus from "./AutomationStatus"


interface AutomationReviewProps {
    label: string;
    automator?: Automator;
}


export default observer(function AutomationReview (props: AutomationReviewProps) {

    const [tab, setTab] = React.useState<"details" | "answers">("details")

    var automator = props.automator 
    automator = store.AutomationStore.getUpdatedAutomator(automator)

    if (!automator) return <CircularProgress style={{width: "20px", height: "20px", margin: "20px auto"}} />

    return (        
        <div className="automation-review">
            <div className="header" style={{marginBottom: "10px"}}>
                <Tabs
                    value={tab}
                    textColor="primary"
                    indicatorColor="primary"
                    variant="fullWidth"
                    style={{width: "100%", borderBottom: "1px solid lightgrey"}}
                    className="automation-review-tab">
                    <Tab onClick={() => setTab("details")} value="details" label="Automation Details" />
                    <Tab onClick={() => setTab("answers")} value="answers" label="Answers" />
                </Tabs>
            </div>

            <div className="automation-review-columns">
                <div className={tab === "details" ? "automation-information" : "automation-information collapse"}>
                    <div style={{display: "flex"}}>
                        <h2 className="collapse">Automation Review</h2>
                        
                        <InlineMenu title="Automation options" opener={<span style={{color: "whitesmoke"}}>...</span>} menuItems={[
                            {
                                label: "Copy input data",
                                action: () => navigator.clipboard.writeText(JSON.stringify(automator.profile, null, "\t"))
                            },
                            {
                                label: "View input data",
                                action: () => AlertModal({body: JSON.stringify(automator.profile, null, "\t"), code: true, size: "xl"})
                            },
                            {
                                label: "View all raw data",
                                action: () => AlertModal({body: JSON.stringify(automator, null, "\t"), code: true, size: "xl"})
                            },
                            {
                                label: "View error page",
                                hidden: !automator.error_page_src,
                                action: () => {
                                    const newTab = window.open();
                                    if (newTab) {
                                        newTab.document.write(automator.error_page_src);
                                    }
                                }
                            },
                            {
                                label: "View log",
                                action: () => {
                                AlertModal({size: "xl", body: (
                                    <div style={{whiteSpace: "pre", fontFamily: "monospace"}}>
                                        <h2>Automation log</h2>
                                        <div style={{maxHeight: "70vh", padding: "0 8px", background: "black", color: "#26d126", overflow: "auto"}}>
                                            {automator.log.map((message, i) => <p key={i}>{message}</p>)}
                                        </div>
                                    </div>
                                )})
                            }}
                        ]} />
                    </div>

                    <AutomationStatus automator={automator} />
                    
                    <div>
                        <h4>Input fields filled</h4>
                        <AutomationStats automator={automator} />
                    </div>
                    
                    {!["INSTANTIATED", "RUNNING"].includes(automator.status) ? (
                        <div>
                            <h4 style={{margin: "8px 0"}}>Resume details</h4>
                            <AutomationResumeDetails automator={automator} />
                        </div>
                    ) : null}
                    
                    {automator.downloads?.length ? (
                        <div>
                            <h4 style={{margin: "8px 0"}}>Downloads</h4>
                            <ul style={{margin: 0}}>
                                {automator.downloads.map((downloadJson, i) => {
                                    var download = JSON.parse(downloadJson)
                                    return (
                                        <li key={i}>
                                            <Button 
                                                style={{
                                                    width: "100%",
                                                    whiteSpace: "pre-wrap",
                                                    lineHeight: "unset",
                                                    justifyContent: "flex-start",
                                                    textAlign: "left"
                                                }}
                                                className="btn btn-sm" 
                                                onClick={() => downloadBase64File(download.display_name, download.binary, download.format)}>
                                                {download.field_name}
                                            </Button>
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    ) : null}

                    {automator.profile?.questionnaires?.length ? (
                        <div>
                            <h4 style={{margin: "8px 0"}}>Questionnaires used</h4>
                            
                            <ul style={{margin: 0}}>
                                {automator.profile.questionnaires.map((qRef, i) => (
                                    <li key={i}>
                                        <small
                                            className="link"
                                            onClick={() => AlertModal({width: "700px", body: <ViewQuestionnaire uid={qRef.value} />})} >
                                            {qRef.label}
                                        </small>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}

                    {automator.profile?.data_source_refs?.length ? (
                        <div>
                            <h4 style={{margin: "8px 0"}}>Data sources</h4>
                            
                            <ul style={{margin: 0}}>
                                {automator.profile.data_source_refs.map((nodeRef: DataSource, i) => (
                                    <li key={i}>
                                        {nodeRef.type === "Questionnaire" ? (
                                            <small className="link" onClick={() => AlertModal({width: "700px", body: <ViewQuestionnaire uid={nodeRef.sourceUid} />})} >
                                                {nodeRef.label ? nodeRef.label : nodeRef.sourceUid}
                                            </small>
                                        ) : (
                                            <small>{nodeRef.label ? nodeRef.label : nodeRef.sourceUid}</small>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>

                <div style={{overflow: "hidden"}} className={tab === "answers" ? "" : "collapse"}>
                    <AutomationAnswers automator={automator} />
                </div>
            </div>
        </div>
    )
})


function AutomationStats ({automator}) {
    
    var genuineColor = Theme.palette.success.main
    var guessedColor = Theme.palette.warning.main
    var falseColor = Theme.palette.error.main

    return (
        <div className="automation-stats">
            <div style={{display: "grid", gridTemplateColumns: "10px 100px 20px", columnGap: "8px", alignItems: "center", marginLeft: "15px"}}>
                <div><div style={{backgroundColor: genuineColor, padding: "5px", borderRadius: "5px"}}></div></div>
                <div>Complete</div>
                <div>{automator.genuine_value_count}</div>
            
                <div><div style={{backgroundColor: guessedColor, padding: "5px", borderRadius: "10px"}}></div></div>
                <div>Needs checking</div>
                <div>{automator.guessed_value_count}</div>
            
                <div><div style={{backgroundColor: falseColor, padding: "5px", borderRadius: "10px"}}></div></div>
                <div>Needs updating</div>
                <div>{automator.false_value_count}</div>
            
                <div></div>
                <div>Total fields</div>
                <div>{automator.total_fields}</div>
            </div>

            <FormAutomationChart
                labels={["Genuine", "Empty", "Fake"]}
                chartData={[
                    automator.genuine_value_count,
                    automator.guessed_value_count,
                    automator.false_value_count
                ]}
                colors={[
                    genuineColor,
                    guessedColor,
                    falseColor,
                ]}
            />
        </div>
    )
}